import React, { useState, useEffect } from 'react';
import { StyleSheet } from 'react-native';
import {
  DNABox,
  DNAText,
  DNAModal,
} from '@alucio/lux-ui';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import InputComponent from 'src/components/Publishers/InputComponent';
import { PromptValues } from '@alucio/aws-beacon-amplify/src/models';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { PromptTypeConfig } from 'src/state/context/PromptValues/PromptValuesStateProvider';
import {
  createPromptValueSchema,
  DEFAULT_PROMPT_VALUE_CHARACTER_LIMIT,
  PromptValueFormData,
} from './promptValueSchema';

const styles = StyleSheet.create({
  modal: {
    maxHeight: '90vh',
    paddingVertical: 12,
  },
  modalBody: {
    width: 800,
  },
  textAreaContainer: {
    minHeight: 400,
    paddingHorizontal: 12,
  },
  errorText: {
    color: colors['color-danger-500'],
  },
});

interface PromptValueModalProps {
  closeModal: () => void;
  onSave: (text: string) => void;
  value?: PromptValues | null; // If provided, it's edit mode
  config: PromptTypeConfig;
  title?: string;
}

const PromptValueModal: React.FC<PromptValueModalProps> = ({
  closeModal,
  onSave,
  value = null,
  config,
  title,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const characterLimit = config.characterLimit === undefined
    ? DEFAULT_PROMPT_VALUE_CHARACTER_LIMIT
    : config.characterLimit;

  const form = useForm<PromptValueFormData>({
    resolver: zodResolver(createPromptValueSchema(characterLimit)),
    mode: 'onSubmit',
    defaultValues: {
      text: value?.value || '',
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = form;

  // Reset form when modal opens or value changes
  useEffect(() => {
    reset({
      text: value?.value || '',
    });
    setIsSaving(false);
  }, [value, reset]);

  const onSubmit = async (data: PromptValueFormData) => {
    setIsSaving(true);
    try {
      await onSave(data.text.trim());
      closeModal();
    } catch (error) {
      form.setError('text', { message: 'Failed to save value' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    reset();
    closeModal();
  };

  return (
    <DNAModal style={styles.modal}>
      <DNAModal.Header onClose={handleCancel}>
        <DNAText h5>
          {title || (value ? config.modalTitles.edit : config.modalTitles.create)}
        </DNAText>
      </DNAModal.Header>

      <DNAModal.Body style={styles.modalBody}>
        <DNABox appearance="col" spacing="sm" fill style={styles.textAreaContainer}>
          <Controller
            name="text"
            control={control}
            render={({ field }) => (
              <InputComponent
                {...field}
                placeHolder={config.placeholder}
                multiline
                numOfLines={5}
                testID="prompt-value-textarea"
                showCharacterCounter={false}
                characterLimit={characterLimit ?? undefined}
                required={false}
                removeMarginPadding
                onBlur={() => {
                  field.onBlur();
                  setIsFocused(false);
                }}
                onFocus={() => setIsFocused(true)}
                inputStyle={{
                  minHeight: 330,
                  paddingTop: 12,
                  ...(errors.text && { borderColor: colors['color-danger-500'] }),
                  ...(isFocused && !errors.text && { borderColor: colors['color-primary-500'] }),
                }}
              />
            )}
          />
          {errors.text && (
            <DNABox style={{ marginTop: 8 }}>
              <DNAText p1 style={styles.errorText}>
                {errors.text.message}
              </DNAText>
            </DNABox>
          )}
        </DNABox>
      </DNAModal.Body>

      <DNAModal.Confirmation
        onCancel={handleCancel}
        cancelLabel="Cancel"
        onAction={handleSubmit(onSubmit)}
        actionLabel={isSaving ? 'Saving...' : 'Save'}
        actionStatus="primary"
      />
    </DNAModal>
  );
};

export default PromptValueModal;
